import React, { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle, ArrowLeft } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import MeetingModal from "../../pages/Lead/AddMeeting";
import EditMeetingModal from "../../pages/Lead/UpdateMeeting";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { styles } from "../../styles/pages/MeetingLead/MeetingTablestyle";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

interface APIMeeting {
  id: number;
  lead_name: string;
  meeting_date: string;
  location: string;
  agenda: string;
  status: string;
}

interface Meeting {
  id: number;
  lead_name: string;
  meeting_date: string;
  location: string;
  agenda: string;
  status: string;
}

const MeetingTable: React.FC = () => {
  const { lead_id } = useParams<{ lead_id: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Meeting | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [postUpdateLoading, setPostUpdateLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showEditMeetingModal, setShowEditMeetingModal] = useState(false);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);
  const [leadName, setLeadName] = useState<string>(""); // Added leadName state

  // Redirect if lead_id is undefined
  useEffect(() => {
    if (!lead_id) {
      toast.error("Invalid lead ID. Redirecting to leads list.");
      navigate("/leads");
    }
  }, [lead_id, navigate]);

  const getAuthToken = useCallback((): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      console.error("No auth token found.");
      navigate("/login");
    }
    return token;
  }, [navigate]);

  const fetchMeetings = useCallback(async () => {
    if (!lead_id) return;

    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/meetings/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            lead_id: lead_id,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status === 200) {
        const formattedMeetings: Meeting[] = Array.isArray(result.data)
          ? result.data.map((meeting: APIMeeting) => ({
            id: meeting.id,
            lead_name: meeting.lead_name,
            meeting_date: meeting.meeting_date,
            location: meeting.location,
            agenda: meeting.agenda,
            status: meeting.status,
          }))
          : [];
        setMeetings(formattedMeetings);
        // Set lead name from the first meeting if available
        setLeadName(formattedMeetings.length > 0 ? formattedMeetings[0].lead_name : "Unknown Lead");
      } else {
        setMeetings([]);
        setLeadName("Unknown Lead");
        setError(result.message || "Failed to fetch meetings.");
        toast.error(result.message || "Failed to fetch meetings.");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setMeetings([]);
      setLeadName("Unknown Lead");
      toast.error("An error occurred while fetching meetings.");
    } finally {
      setLoading(false);
      setPostUpdateLoading(false);
    }
  }, [lead_id, getAuthToken]);

  const handleMeetingUpdated = useCallback(async () => {
    setPostUpdateLoading(true);
    setShowEditMeetingModal(false);
    await fetchMeetings();
  }, [fetchMeetings]);

  const handleMeetingCreated = useCallback(async () => {
    setPostUpdateLoading(true);
    setShowMeetingModal(false);
    await fetchMeetings();
  }, [fetchMeetings]);

  const handleDeleteClick = (meetingId: number) => {
    setSelectedMeetingId(meetingId);
    openModal();
  };

  const handleEditClick = (meetingId: number) => {
    setSelectedMeetingId(meetingId);
    setShowEditMeetingModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedMeetingId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/meetings/delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: selectedMeetingId }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status === 200) {
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
        setMeetings((prevMeetings) => {
          const updatedMeetings = prevMeetings.filter(
            (meeting) => meeting.id !== selectedMeetingId
          );
          if (updatedMeetings.length > 0 && displayedMeetings.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updatedMeetings;
        });
        closeModal();
      } else {
        toast.error(result.message || "Failed to delete meeting.");
      }
    } catch (error) {
      console.error("Network error:", error);
      toast.error("An unexpected error occurred while deleting the meeting.");
    }
  };

  const filteredMeetings = meetings.filter((meeting) =>
    Object.values(meeting)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedMeetings = [...filteredMeetings].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    return typeof valA === "string" && typeof valB === "string"
      ? sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
      : sortOrder === "asc"
        ? (valA as number) - (valB as number)
        : (valB as number) - (valA as number);
  });

  const displayedMeetings = sortedMeetings.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredMeetings.length / rowsPerPage);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof Meeting) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredMeetings
      : sortColumn
        ? sortedMeetings
        : meetings;

    if (!dataToDownload.length) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((meeting) => ({
      "Lead Name": meeting.lead_name,
      "Meeting Date & Time": meeting.meeting_date,
      Location: meeting.location,
      Agenda: meeting.agenda,
      Status: meeting.status,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [[`Meeting Data List for ${leadName}`]], {
      origin: "A1",
    });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          `Meeting Data List for ${leadName}`.length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Meetings");
    XLSX.writeFile(
      workbook,
      searchTerm ? `MeetingData_${searchTerm}.xlsx` : `MeetingData_${leadName}.xlsx`
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    if (lead_id) {
      fetchMeetings();
    }
  }, [fetchMeetings, lead_id]);

  // Render nothing or a loading state if lead_id is undefined
  if (!lead_id) {
    return <div className="text-center py-4">Redirecting...</div>;
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Meeting List For {leadName}</h3>
          <Link to="/lead-list" className={styles.back}>
            <ArrowLeft className="inline-block mr-1" size={16} />
            Back
          </Link>
        </div>

        <div className={styles.tableCard}>
          <div className={styles.headerControls}>
            <RowsPerPageSelector
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
            <div className={styles.controlsInner}>
              <div className={styles.searchContainer}>
                <Search className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search..."
                  className={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className={styles.downloadButton} onClick={handleDownload}>
                Download
                <DownloadIcon className={styles.downloadIcon} />
              </button>
              <button
                onClick={() => setShowMeetingModal(true)}
                className={styles.addButton}
              >
                Add New Meeting
                <PlusIcon />
              </button>
              <MeetingModal
                state={{ lead_id }}
                isOpen={showMeetingModal}
                onClose={() => setShowMeetingModal(false)}
                onMeetingCreated={handleMeetingCreated}
              />
            </div>
          </div>
          {error && <p className={styles.errorText}>{error}</p>}

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.theadRow}>
                  <th
                    className={styles.thName}
                    onClick={() => handleSort("lead_name")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Lead Name</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("meeting_date")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Meeting Date & Time</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("location")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Location</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thAgenda}
                    onClick={() => handleSort("agenda")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Agenda</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("status")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Status</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th className={styles.thAction}>
                    <p className={styles.thText}>Action</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading || postUpdateLoading ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={8} className="text-center py-4">
                      <Loader />
                    </td>
                  </tr>
                ) : meetings.length === 0 ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={6} className={styles.noDataText}>
                      Your meeting list is currently empty. Use the ‘Add New Meeting’ button to create one.
                    </td>
                  </tr>
                ) : filteredMeetings.length === 0 ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={6} className={styles.noDataText}>
                      No records match your search.
                    </td>
                  </tr>
                ) : (
                  displayedMeetings.map((meeting) => (
                    <tr key={meeting.id} className={styles.tbodyRow}>
                      <td className={styles.tdName}>{meeting.lead_name}</td>
                      <td className={styles.tdRole}>{meeting.meeting_date}</td>
                      <td className={styles.tdRole}>{meeting.location}</td>
                      <td className={styles.tdRole}>{meeting.agenda}</td>
                      <td className={styles.tdRole}>{meeting.status}</td>
                      <td className={styles.tdAction}>
                        <div className={styles.actionContainer}>
                          <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                            <button
                              onClick={() => handleEditClick(meeting.id)}
                              className={styles.editButton}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="text-white text-sm"
                              />
                            </button>
                          </Tooltip>
                          {selectedMeetingId === meeting.id && (
                            <EditMeetingModal
                              state={{
                                lead_id,
                                meeting_id: selectedMeetingId.toString(),
                                lead_name: meeting.lead_name,
                              }}
                              isOpen={showEditMeetingModal}
                              onClose={() => setShowEditMeetingModal(false)}
                              onMeetingUpdated={handleMeetingUpdated}
                            />
                          )}
                          <Tooltip content="Delete" bgColor="bg-red-600">
                            <button
                              className={styles.deleteButton}
                              onClick={() => handleDeleteClick(meeting.id)}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="text-white text-sm"
                              />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            filteredLength={filteredMeetings.length}
            handlePageChange={handlePageChange}
          />
        </div>

        <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
          <button
            onClick={closeModal}
            className={styles.modalCloseButton}
          ></button>
          <div className="flex justify-center">
            <div className={styles.modalIconContainer}>
              <FontAwesomeIcon icon={faTrash} className={styles.modalIcon} />
            </div>
          </div>
          <h4 className={styles.modalTitle}>Confirm Delete</h4>
          <p className={styles.modalText}>
            Are you sure you want to delete this meeting?
          </p>
          <div className={styles.modalButtonContainer}>
            <button onClick={closeModal} className={styles.modalCancelButton}>
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </Modal>

        <Modal
          isOpen={isDeleteSuccessModalOpen}
          onClose={() => setDeleteSuccessModalOpen(false)}
          className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
        >
          <div className="flex justify-center">
            <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
          <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
            Meeting Deleted Successfully!
          </h4>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setDeleteSuccessModalOpen(false)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              OK
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default MeetingTable;